import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { COMMON_FETCH_HEADERS } from "@/lib/utils";
import { env } from "@/env";
import { z } from "zod/v4";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { QuickLinks } from "./-components/admin-payments-paymentId/quick-links";
import { BasicPaymentInfo } from "./-components/admin-payments-paymentId/basic-payment-info";
import { UserAgentInfo } from "./-components/admin-payments-paymentId/user-agent-info";
import { PaymentTimeline } from "./-components/admin-payments-paymentId/payment-timeline";
import { RefundTimeline } from "./-components/admin-payments-paymentId/refund-timeline";
import { MetadataSection } from "./-components/admin-payments-paymentId/metadata-section";
import { PayerDetails } from "./-components/admin-payments-paymentId/payer-details";
import { PayeeInformation } from "./-components/admin-payments-paymentId/payee-information";
import { ConfirmationOfPayer } from "./-components/admin-payments-paymentId/confirmation-of-payer";
import { MerchantProvidedPayerDetails } from "./-components/admin-payments-paymentId/merchant-provided-payer-details";

const GetPaymentDiagnosticsResponse = z.object({
  createdAt: z.string(),
  institutionId: z.string().nullable(),
  paymentId: z.string(),
  merchantPaymentId: z.string(),
  paymentStatus: z.string(),
  paymentRequest: z.object({
    amount: z.number(),
    currency: z.string(),
    reference: z.string(),
  }),
  isToVirtualAccount: z.boolean(),
  canStartRefund: z.boolean(),
  amountRefunded: z.number(),
  amountRefunding: z.number(),
  refunds: z.array(
    z.object({
      refundId: z.string(),
      createdAtUtc: z.string(),
      updatedAtUtc: z.string(),
      refundPaymentReference: z.string().nullable(),
      refundPaymentAmount: z.number(),
      refundPaymentCurrency: z.string(),
      refundStatus: z.string(),
    }),
  ),
  payer: z
    .object({
      name: z.string().nullable(),
      email: z.string().nullable(),
      accountIdentifier: z
        .object({
          type: z.string().nullable(),
          accountNumber: z.string().nullable(),
          sortCode: z.string().nullable(),
          iban: z.string().nullable(),
          bic: z.string().nullable(),
        })
        .nullable(),
      address: z
        .object({
          line1: z.string().nullable(),
          line2: z.string().nullable(),
          country: z.string().nullable(),
          town: z.string().nullable(),
          postCode: z.string().nullable(),
        })
        .nullable(),
      payerIP: z.string().nullable(),
    })
    .nullable(),
  metadata: z.any().nullable(),
  payerIP: z.string().nullable(),
  tracingId: z.string(),
  confirmationOfPayer: z
    .object({
      enabled: z.boolean().optional(),
      score: z.number().nullable().optional(),
      riskCategory: z.string().nullable().optional(),
      namesFromMerchant: z.string().nullable().optional(),
      namesFromYapily: z.string().nullable().optional(),
      namesFromVaProvider: z.string().nullable().optional(),
      calculatedAt: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  merchantProvidedPayerDetails: z
    .object({
      email: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
      phone: z.string().nullable().optional(),
      account: z
        .object({
          holderName: z.string().nullable().optional(),
          sortCode: z.string().nullable().optional(),
          accountNumber: z.string().nullable().optional(),
          iban: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      address: z
        .object({
          addressLine1: z.string().nullable().optional(),
          addressLine2: z.string().nullable().optional(),
          town: z.string().nullable().optional(),
          postCode: z.string().nullable().optional(),
          countryCode: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  diagnostics: z.object({
    userAgentData: z.object({
      userAgent: z.string().nullable(),
      appName: z.string().nullable(),
      deviceInfo: z.string().nullable(),
      volumeVersion: z.string().nullable(),
      agentType: z.string(),
    }),
    dataDogLogsLink: z.string(),
    dataDogDashboardLink: z.string(),
    paymentTimelineEvents: z.array(
      z.object({
        date: z.string(),
        eventType: z.string(),
        properties: z.any(),
      }),
    ),
    refundTimelineEvents: z
      .array(
        z.object({
          date: z.string(),
          eventType: z.string(),
          properties: z.any(),
          refundId: z.string(),
        }),
      )
      .optional(),
    payeeData: z.object({
      accountHolderName: z.string(),
      accountIdentification1: z.object({
        type: z.string(),
        number: z.string(),
      }),
      accountIdentification2: z
        .object({
          type: z.string(),
          number: z.string(),
        })
        .nullable(),
      address: z
        .object({
          streetName: z.string().nullable(),
          buildingNumber: z.string().nullable(),
          postCode: z.string().nullable(),
          townName: z.string().nullable(),
          country: z.string().nullable(),
          department: z.string().nullable(),
          subDepartment: z.string().nullable(),
          addressType: z.string().nullable(),
        })
        .nullable(),
      vaProviderType: z.string().nullable(),
      modulrCustomerId: z.string().nullable(),
      modulrAccountId: z.string().nullable(),
      modulrDefaultPayoutBeneficiaryId: z.string().nullable(),
      modulrIsSandboxAccount: z.boolean().nullable(),
      modulrForLimitedVirtualAccount: z.boolean().nullable(),
    }),
    payerData: z
      .object({
        name: z.string().nullable(),
        accountIdentification1: z
          .object({
            type: z.string(),
            number: z.string(),
          })
          .nullable(),
        accountIdentification2: z
          .object({
            type: z.string(),
            number: z.string(),
          })
          .nullable(),
      })
      .nullable(),
    applicationName: z.string(),
    applicationId: z.string(),
    paymentType: z.string(),
    statusChanges: z.any().nullable(),
    merchantPortalLink: z.string(),
    adminPortalLink: z.string(),
    isHeld: z.boolean(),
    heldAt: z.string().nullable(),
    payerEmail: z.string().nullable(),
    paymentIntentId: z.string().nullable(),
    selectedYapilyClientName: z.string().nullable(),
    selectedYapilyApplicationId: z.string().nullable(),
    payeeVirtualAccountType: z.string(),
    isToFullVirtualAccount: z.boolean(),
    isToLimitedVirtualAccount: z.boolean(),
    isRefunded: z.boolean(),
    refundData: z
      .object({
        accountHolderName: z.string(),
        accountIdentification1: z.object({
          type: z.string(),
          number: z.string(),
        }),
        accountIdentification2: z
          .object({
            type: z.string(),
            number: z.string(),
          })
          .nullable(),
      })
      .nullable(),
    payerCountry: z.string().nullable(),
    payerLanguage: z.string().nullable(),
    paymentLandingPage: z.string().nullable(),
    returnToMerchantPageUrl: z.string().nullable(),
  }),
});

export const Route = createFileRoute(
  "/(app)/_admin-layout/admin_/payments_/$paymentId",
)({
  component: RouteComponent,
});

// Main Route Component - Now much simpler with reduced cognitive complexity
function RouteComponent() {
  const context = Route.useRouteContext();
  const { paymentId } = Route.useParams();
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);

  const paymentQuery = useQuery({
    queryKey: ["payments", paymentId],
    queryFn: async () => {
      const response = await fetch(
        `${env.VITE_VOLUME_URL}/api/payments/${paymentId}/diagnostics`,
        {
          headers: {
            Authorization: `Bearer ${context.accessToken}`,
            ...COMMON_FETCH_HEADERS,
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch payment");
      }
      const data = await response.json();
      return GetPaymentDiagnosticsResponse.parse(data);
    },
  });

  if (paymentQuery.isLoading) {
    return (
      <div className="space-y-2">
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <p>Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (paymentQuery.error) {
    return (
      <div className="space-y-2">
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <p className="text-red-600">
            Error loading payment: {paymentQuery.error.message}
          </p>
          <Button onClick={() => paymentQuery.refetch()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!paymentQuery.data) {
    return null;
  }

  return (
    <div className="space-y-2">
      <button
        onClick={() => {
          window.history.back();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            window.history.back();
          }
        }}
        className="text-blue-600 hover:underline flex items-center hover:cursor-pointer bg-transparent border-none p-0 font-inherit"
        type="button"
        aria-label="Go back to previous page"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 mr-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        Back
      </button>

      {/* Section 1: Payment Details */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">Payment Details</h2>
        <QuickLinks data={paymentQuery.data} />
        <BasicPaymentInfo data={paymentQuery.data} />
        <RefundTimeline data={paymentQuery.data} />
        <UserAgentInfo data={paymentQuery.data} />

        {/* Payer, Payee and Metadata Information */}
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <PayerDetails data={paymentQuery.data} />
            </div>

            <div className="space-y-4">
              {/* Confirmation of Payer Section */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-lg font-semibold mb-3">
                  Confirmation of Payer
                </h3>
                {paymentQuery.data.confirmationOfPayer ? (
                  <ConfirmationOfPayer
                    payerInfo={paymentQuery.data.confirmationOfPayer}
                  />
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No confirmation of payer data available
                  </p>
                )}
              </div>

              <MerchantProvidedPayerDetails data={paymentQuery.data} />
            </div>

            <PayeeInformation data={paymentQuery.data} />
          </div>
          <MetadataSection
            data={paymentQuery.data}
            isExpanded={isMetadataExpanded}
            onToggle={() => setIsMetadataExpanded(!isMetadataExpanded)}
          />
        </div>
      </div>

      {/* Section 2: Payment Timeline */}
      <PaymentTimeline data={paymentQuery.data} />
    </div>
  );
}
